import { Box, Button, Typography } from "@mui/material";
import React from "react";
import { useDrag, useDrop } from "react-dnd";
import Xarrow from "react-xarrows";
import { AudioApi } from "../api/AudioApi";
import { MatchEnum } from "../api/DialogPromptApi";
import { IVocabularyWord } from "../api/VocabularyWordApi";
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from "../context/AppBarContext";
import { useAudioPlayer } from "../context/AudioPlayerContext";
import { TeacherAudioActions, getSelectedTeacherSpeakerRole } from "./TeacherAudioReportButton";

// type WordItem = { id: number; text: string };
export type MatchingGameMatch = { leftId: number; rightId: number };
type MatchingGameSide = "left" | "right";
type MatchingGameItem = { id: number; side: MatchingGameSide };

const HIGHLIGHT_COLOR = '#FCCD56';
const SELECTED_COLOR = '#0062FF';

interface MatchingGameProp {
    instanceId: string;
    leftMatch: MatchEnum;
    rightMatch: MatchEnum;
    leftList: IVocabularyWord[];
    rightList: IVocabularyWord[];
    matchList: MatchingGameMatch[];
    setMatchList: React.Dispatch<React.SetStateAction<MatchingGameMatch[]>>;
    readOnly?: boolean;
}

const MatchingGame: React.FC<MatchingGameProp> = ({ instanceId, leftMatch, rightMatch, leftList, rightList, matchList, setMatchList, readOnly = false }) => {

    const [hoveredItem, setHoveredItem] = React.useState<MatchingGameItem | null>(null);
    const [selectedItem, setSelectedItem] = React.useState<MatchingGameItem | null>(null);

    // A new round hands us new word lists - drop any selection left over from the previous one.
    React.useEffect(() => {
        setSelectedItem(null);
    }, [leftList, rightList]);

    const applyMatch = React.useCallback((leftId: number, rightId: number) => {
        // Prevent duplicate match
        setMatchList((prev) => [
            ...prev.filter((m) => m.leftId !== leftId && m.rightId !== rightId),
            { leftId, rightId },
        ]);
    }, [setMatchList]);

    const handleMatch = (draggedId: number, dropId: number, side: MatchingGameSide) => {
        // console.log('handleMatch', draggedId, dropId, side)
        if (side === "left") {
            applyMatch(draggedId, dropId);
        } else {
            applyMatch(dropId, draggedId);
        }
        setSelectedItem(null);
    };

    // Click-to-match: pick a card on one side, then pick its match on the other side.
    // Clicking the already selected card clears it; clicking another card on the same
    // side moves the selection there.
    const handleSelect = (id: number, side: MatchingGameSide, allowDeselect = true) => {
        if (readOnly) {
            return;
        }

        if (!selectedItem) {
            setSelectedItem({ id, side });
            return;
        }

        if (selectedItem.side === side) {
            setSelectedItem(allowDeselect && selectedItem.id === id ? null : { id, side });
            return;
        }

        applyMatch(
            side === "left" ? id : selectedItem.id,
            side === "right" ? id : selectedItem.id,
        );
        setSelectedItem(null);
    };

    return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            {!readOnly && (
                <Typography variant="caption" color="text.secondary" textAlign="center">
                    Drag a card onto its match, or click one card and then its match.
                </Typography>
            )}
            <Box display="flex" position="relative"
                justifyContent="space-between" // spreads columns apart across full width
                gap={12}
            >
                <Column
                    wordList={leftList}
                    side="left"
                    matchType={leftMatch}
                    onDropMatch={handleMatch}
                    hoveredItem={hoveredItem}
                    setHoveredItem={setHoveredItem}
                    selectedItem={selectedItem}
                    onSelect={handleSelect}
                    matchList={matchList}
                    readOnly={readOnly}
                    instanceId={instanceId} />
                <Column
                    wordList={rightList}
                    side="right"
                    matchType={rightMatch}
                    onDropMatch={handleMatch}
                    hoveredItem={hoveredItem}
                    setHoveredItem={setHoveredItem}
                    selectedItem={selectedItem}
                    onSelect={handleSelect}
                    matchList={matchList}
                    readOnly={readOnly}
                    instanceId={instanceId} />
                {matchList.map((match) => {
                    const isActive = [hoveredItem, selectedItem].some((item) =>
                        item &&
                        (
                            (item.side === "left" && match.leftId === item.id) ||
                            (item.side === "right" && match.rightId === item.id)
                        )
                    );
                    // console.log('Rendering Xarrow:', `left-${match.leftId}`, `right-${match.rightId}`);

                    return (
                        <Xarrow
                            key={`${match.leftId}-${match.rightId}`}
                            start={`${instanceId}-left-${match.leftId}`}
                            end={`${instanceId}-right-${match.rightId}`}
                            strokeWidth={2}
                            color={isActive ? HIGHLIGHT_COLOR : 'blue'}
                            path="straight"       // 👈 forces straight lines
                            showHead={true}       // optional: keep arrowhead
                            dashness={false}      // optional: solid line
                        />
                    )
                })}
            </Box>
        </Box>
    );
};

interface ColumnProps {
    instanceId: string;
    wordList: IVocabularyWord[];
    side: MatchingGameSide;
    matchType: MatchEnum;
    onDropMatch: (draggedId: number, dropId: number, side: MatchingGameSide) => void;
    hoveredItem: MatchingGameItem | null;
    setHoveredItem: (hover: MatchingGameItem | null) => void;
    selectedItem: MatchingGameItem | null;
    onSelect: (id: number, side: MatchingGameSide, allowDeselect?: boolean) => void;
    matchList: MatchingGameMatch[];
    readOnly: boolean;
}

const Column: React.FC<ColumnProps> = ({ wordList, side, matchType, onDropMatch, hoveredItem, setHoveredItem, selectedItem, onSelect, matchList, readOnly, instanceId }) => {
    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {wordList.map((word) => (
                <DraggableItem
                    key={word.id}
                    word={word}
                    side={side}
                    matchType={matchType}
                    onDropMatch={onDropMatch}
                    hoveredItem={hoveredItem}
                    setHoveredItem={setHoveredItem}
                    selectedItem={selectedItem}
                    onSelect={onSelect}
                    matchList={matchList}
                    readOnly={readOnly}
                    instanceId={instanceId}
                />
            ))}
        </Box>
    );
};

interface DraggableProps {
    instanceId: string;
    word: IVocabularyWord;
    side: MatchingGameSide;
    matchType: MatchEnum;
    onDropMatch: (draggedId: number, dropId: number, side: MatchingGameSide) => void;
    hoveredItem: MatchingGameItem | null;
    setHoveredItem: (hover: MatchingGameItem | null) => void;
    selectedItem: MatchingGameItem | null;
    onSelect: (id: number, side: MatchingGameSide, allowDeselect?: boolean) => void;
    matchList: MatchingGameMatch[];
    readOnly: boolean;
}

const DraggableItem: React.FC<DraggableProps> = ({ instanceId, word, side, matchType, onDropMatch, hoveredItem, setHoveredItem, selectedItem, onSelect, matchList, readOnly }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const { playAudio } = useAudioPlayer();

    const [{ isDragging }, drag] = useDrag({

        type: "item",
        item: { id: word.id, side },
        canDrag: !readOnly,
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [{ isOver }, drop] = useDrop({
        accept: 'item',
        canDrop: () => !readOnly,
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: true }),
        }),
        drop: (dragged: { id: number; side: MatchingGameSide }) => {
            if (dragged.side !== side) {
                onDropMatch(dragged.id, word.id, dragged.side);
            }
            setHoveredItem(null); // 👈 Clear hover after drop
        },
    });

    const isHoveredSelf =
        hoveredItem && hoveredItem.id === word.id && hoveredItem.side === side;

    const isHoveredOpposite =
        hoveredItem &&
        hoveredItem.side !== side &&
        (
            (side === "left" && matchList.some(m => m.leftId === word.id && m.rightId === hoveredItem.id)) ||
            (side === "right" && matchList.some(m => m.rightId === word.id && m.leftId === hoveredItem.id))
        );

    const isSelected = Boolean(selectedItem && selectedItem.id === word.id && selectedItem.side === side);
    // Once a card is picked, the opposite column becomes the set of things to click next.
    const isSelectableTarget = Boolean(selectedItem && selectedItem.side !== side);
    const isHighlighted = Boolean(isDragging || isOver || isHoveredSelf || isHoveredOpposite || isSelected);

    // Shared look for the button/audio/image variants below.
    const itemSx = {
        opacity: isDragging ? 0.5 : 1,
        cursor: readOnly ? 'default' : 'pointer',
        backgroundColor: isHighlighted ? HIGHLIGHT_COLOR : undefined,
        color: isHighlighted ? 'black' : undefined,
        outline: isSelected
            ? `3px solid ${SELECTED_COLOR}`
            : isSelectableTarget
                ? `2px dashed rgba(0, 98, 255, 0.45)`
                : undefined,
        outlineOffset: (isSelected || isSelectableTarget) ? '2px' : undefined,
        '&:hover': {
            backgroundColor: HIGHLIGHT_COLOR,
        },
    };

    // The audio card renders its report dialog as a React child, so events from the
    // dialog bubble here even though the portal puts it outside this card in the DOM.
    const isEventFromCard = (event: React.SyntheticEvent) =>
        event.currentTarget instanceof Node && event.currentTarget.contains(event.target as Node);

    const handleClick = (event: React.MouseEvent) => {
        if (isEventFromCard(event)) {
            onSelect(word.id, side);
        }
    };

    // Enter/Space on the non-button variants, so the game is keyboard reachable too.
    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (isEventFromCard(event) && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onSelect(word.id, side);
        }
    };

    const getAudio = (sentenceScoreId: number) => {
        const audioPromise = AudioApi.getTeacherSentence(sentenceScoreId)

        audioPromise
            .then(response => {
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Getting male audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(blob => {
                playAudio(blob)
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    };

    let text = undefined
    let image = undefined
    let audio_sentence_id = -1
    if (matchType == MatchEnum.FirstWordCharacter) {
        text = word.word
    } else if (matchType == MatchEnum.FirstWordPinyin) {
        text = word.word_pinyin.join(" ")
    } else if (matchType == MatchEnum.SecondWordCharacter) {
        text = word.audio_sentence
    } else if (matchType == MatchEnum.SecondWordPinyin) {
        text = word.audio_sentence_pinyin.join(" ")
    } else if (matchType == MatchEnum.SecondWordAudio) {
        audio_sentence_id = word.audio_sentence_id
    } else if (matchType == MatchEnum.Image) {
        image = word.image ? word.image : word.image_url
    } else if (matchType == MatchEnum.English) {
        text = word.english_text
    } else {
        throw Error(`Unsupported MatchEnum ${matchType}`)
    }

    return (<>
        {text && (
            <Button
                id={`${instanceId}-${side}-${word.id}`} // This is what Xarrow needs
                ref={(node: HTMLButtonElement | null) => {
                    if (node) drag(drop(node)); // This is what DnD needs
                }}
                variant="contained"
                aria-pressed={isSelected}
                onClick={handleClick}
                onMouseEnter={() => setHoveredItem({ id: word.id, side })}
                onMouseLeave={() => setHoveredItem(null)}
                sx={(theme) => ({
                    ...itemSx,
                    // The global button theme uses 16px of vertical padding. Keep
                    // that desktop sizing, but compact matching cards in the mobile
                    // layout so the game and Submit button fit in the action area.
                    [theme.breakpoints.down('md')]: {
                        minWidth: 0,
                        padding: theme.spacing(0.5, 1),
                    },
                })}
            >
                <Typography
                    sx={(theme) => ({
                        [theme.breakpoints.down('md')]: {
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                        },
                    })}
                >
                    {text}
                </Typography>
            </Button>
        )}
        {audio_sentence_id >= 0 && (
            <Box
                id={`${instanceId}-${side}-${word.id}`} // This is what Xarrow needs
                ref={(node: HTMLButtonElement | null) => {
                    if (node) drag(drop(node)); // This is what DnD needs
                }}
                role={readOnly ? undefined : "button"}
                tabIndex={readOnly ? undefined : 0}
                aria-pressed={isSelected}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                onMouseEnter={() => setHoveredItem({ id: word.id, side })}
                onMouseLeave={() => setHoveredItem(null)}
                sx={{
                    ...itemSx,
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1,          // clickable surface around the speaker icon
                    borderRadius: 1,
                }}
            >
                <TeacherAudioActions
                    context={{
                        sentenceId: audio_sentence_id,
                        dialogPromptId: word.dialog_prompt_id,
                        textToSpeechSpeakerId: getSelectedTeacherSpeakerRole(),
                        audioText: word.audio_sentence,
                        audioRole: 'matching_game_word_audio',
                    }}
                    // The play button swallows the click, so selecting here keeps the
                    // card pickable without a second, separate tap target.
                    onPlay={() => {
                        getAudio(audio_sentence_id)
                        onSelect(word.id, side, false)
                    }}
                />
            </Box>
        )}
        {image && (
            <Box
                id={`${instanceId}-${side}-${word.id}`} // This is what Xarrow needs
                ref={(node: HTMLButtonElement | null) => {
                    if (node) drag(drop(node)); // This is what DnD needs
                }}
                role={readOnly ? undefined : "button"}
                tabIndex={readOnly ? undefined : 0}
                aria-pressed={isSelected}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                onMouseEnter={() => setHoveredItem({ id: word.id, side })}
                onMouseLeave={() => setHoveredItem(null)}
                sx={{
                    ...itemSx,
                    display: 'inline-flex',
                    borderRadius: 1,
                }}
            >
                <img
                    src={word.image ? word.image : word.image_url ? word.image_url : undefined}
                    alt="Uploaded"
                    style={{
                        maxHeight: '64px',
                        maxWidth: '64px',
                        height: 'auto',
                        width: 'auto',
                        display: 'block',
                    }}
                />
            </Box>
        )}

    </>);
};

export default MatchingGame;
